import React, { useRef, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import BasicSchoolProfile from "./BasicSchoolProfile";
import InfrastructureForm from "./InfrastructureForm";
import LearningFacilities from "./LearningFacilities";
import AdministrativeCommunity from "./AdministrativeCommunity";
import AdditionalNotes from "./AdditionalNotes";
import { fetchData } from "../../utils/functions";

const SchoolAssessments = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const tabs = [
    { label: "School Information", permission: "school-assessment-update" },
    {
      label: "Infrastructure & Physical Environment",
      permission: "infrastructure-physical-enviroment-update",
    },
    { label: "Learning Facilities", permission: "learning-facility-update" },
    {
      label: "Administrative & Community Engagement",
      permission: "administrative-community-update",
    },
    {
      label: "Additional Notes",
      permission: "additional-notes-update",
    },
  ];

  const [activeTab, setActiveTab] = useState("School Information");
  const scrollRef = useRef(null);
  const [isLeftClicked, setIsLeftClicked] = useState(false);
  const [isRightClicked, setIsRightClicked] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [loader, setLoader] = useState(false);
  const [schoolData, setSchoolData] = useState(null);
  const [isDqaApproved, setIsDqaApproved] = useState(false);

  const scrollTabs = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });

      if (direction === "left") {
        setIsLeftClicked(true);
        setTimeout(() => setIsLeftClicked(false), 200);
      } else {
        setIsRightClicked(true);
        setTimeout(() => setIsRightClicked(false), 200);
      }
    }
  };

  const fetchSchoolAssessment = async () => {
    try {
      setLoader(true);
      setIsFetching(true);

      const response = await fetchData(
        `school-fetchById/${id}`,
        "GET",
        null,
        {},
        {},
        navigate
      );

      if (response?.data) {
        setSchoolData(response.data);

        // Check for DQA approval at root level
        const isDqaApproved =
          response.data?.verified_from_dqa === 1 ||
          response.data?.verified_from_dqa === "1";

        setIsDqaApproved(isDqaApproved);
      }
    } catch (error) {
      console.error("Error fetching school data:", error);
      toast.error("Failed to load school data.");
    } finally {
      setLoader(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchSchoolAssessment();
  }, [id]);

  const visibleTabs = tabs.filter((tab) =>
    permissions.includes(tab.permission)
  );

  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].label);
    }
  }, [visibleTabs, activeTab]);

  // Disable form fields and show appropriate message when DQA approved
  const handleTabClick = (tabLabel) => {
    setActiveTab(tabLabel);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-[1.475rem] text-black font-bold">
          School Assessment
        </h1>
        {isDqaApproved && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
            Approved from DQA
          </span>
        )}
      </div>

      {/* DQA Approval Message */}
      {isDqaApproved && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-blue-600 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-blue-700 font-medium">
                This assessment has been approved by Data Quality Assurance
                (DQA)
              </p>
              <p className="text-blue-600 text-sm mt-1">
                All sections are now read-only. No further edits can be made.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        {/* Tabs Navigation */}
        <div className="flex items-center rounded-t-lg bg-[#fdf5ec] relative">
          {/* Left Scroll Button */}
          <button
            onClick={() => scrollTabs("left")}
            className={`text-xl px-4 py-2 font-bold rounded-lg transition-colors ${
              isLeftClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            «
          </button>

          {/* Scrollable Tabs */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-hidden scrollbar-hide"
          >
            {visibleTabs.map((tab) => (
              <button
                key={tab.label}
                onClick={() => handleTabClick(tab.label)}
                className={`px-10 py-2 font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.label
                    ? "bg-[#caa316] text-white"
                    : isDqaApproved
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : "text-black hover:bg-gray-100"
                }`}
                style={{
                  borderTopLeftRadius: "13px",
                  borderTopRightRadius: "30px",
                }}
                disabled={isDqaApproved}
              >
                {tab.label}
                {isDqaApproved && activeTab !== tab.label && (
                  <span className="ml-2 text-xs opacity-75">(View Only)</span>
                )}
              </button>
            ))}
          </div>

          {/* Right Scroll Button */}
          <button
            onClick={() => scrollTabs("right")}
            className={`ml-auto text-xl px-4 py-2 rounded-lg font-bold transition-colors ${
              isRightClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            »
          </button>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-b-lg shadow-md p-6">
          {loader ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : (
            <>
              {activeTab === "School Information" && (
                <BasicSchoolProfile
                  data={schoolData?.SchoolProfile}
                  isDqaApproved={isDqaApproved}
                />
              )}
              {activeTab === "Infrastructure & Physical Environment" && (
                <InfrastructureForm
                  data={schoolData?.schoolInfrastructure}
                />
              )}
              {activeTab === "Learning Facilities" && (
                <LearningFacilities
                  data={schoolData?.schoolFacility}
                  isDqaApproved={isDqaApproved}
                />
              )}
              {activeTab === "Administrative & Community Engagement" && (
                <AdministrativeCommunity
                  data={schoolData?.SchoolAdministrative}
                  isDqaApproved={isDqaApproved}
                />
              )}
              {activeTab === "Additional Notes" && (
                <AdditionalNotes
                  data={schoolData?.SchoolAdditionalNotes}
                  schoolProfile={schoolData?.SchoolProfile}
                  schoolProfileId={schoolData?.SchoolProfile?.school_profile_id}
                  isDqaApproved={isDqaApproved}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolAssessments;
